import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify"; // ✅ Import Toastify
import "react-toastify/dist/ReactToastify.css";
import LoadingOverlay from "../components/LoadingOverlay";
import _ from "lodash";

import CreateQuotationForm from "../components/CreateQuotationForm";
import BackButton from "../components/BackButton";
import ItemsForm from "../components/ItemsForm";
import QuotationPreview from "../components/QuotationPreview";
import { pdf } from "@react-pdf/renderer";
import bankAccounts from "../data/bankAccounts.json";
import { apiURL } from "../config/config";
import { upsertCachedQuotation } from "../utils/quotationCache";

// ✅ ฟังก์ชันปัดเศษทศนิยม 2 ตำแหน่ง (ปัดขึ้นถ้าหลักที่ 3 >= 5)
const roundUp = (num) => {
  return (num * 100) % 1 >= 0.5 ? _.ceil(num, 2) : _.round(num, 2);
};

const requiredFields = [
  ["title", "Title"],
  ["clientId", "Client"],
  ["salePerson", "Create By"],
  ["documentDate", "Document Date"],
  ["productName", "Product Name"],
  ["startDate", "Start Date"],
  ["endDate", "End Date"],
  ["type", "Type"],
];

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const CreateQuotation = () => {
  const navigate = useNavigate();

  const [quotationData, setQuotationData] = useState({
    title: "",
    client: "",
    clientId: "",
    salePerson: "",
    documentDate: "",
    productName: "",
    projectName: "",
    period: "",
    startDate: "",
    endDate: "",
    createBy: "",
    proposedBy: "",
    type: "M",
    fee: 0,
    items: [],
    discount: 0,
    CreditTerm: 30,
    createdByUser: "",
    totalBeforeFee: 0,
    netAmount: 0,
    remark: "",
  });

  const [item, setItem] = useState({
    description: "",
    unit: 1,
    unitPrice: "",
  });
  const [isDetailedForm, setIsDetailedForm] = useState(false); // ✅ Default เป็น Standard Form
  const [isSpecialForm, setIsSpecialForm] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [itemErrors, setItemErrors] = useState({});

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.username) {
      setQuotationData((prev) => ({
        ...prev,
        createdByUser: user.username,
        department: user.department || "",
        team: user.team || "",
        teamGroup: user.teamGroup || "",
      }));
    }
  }, []);

  useEffect(() => {
    const totalBeforeFee = quotationData.items.reduce(
      (sum, itm) => sum + itm.unit * itm.unitPrice,
      0
    );
    const netAmount =
      totalBeforeFee - quotationData.discount + quotationData.fee;
    setQuotationData((prev) => ({ ...prev, totalBeforeFee, netAmount }));
  }, [quotationData.items, quotationData.discount, quotationData.fee]);

  const handleItemChange = (field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setItemErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const addItem = () => {
    const nextItemErrors = {};
    if (isBlank(item.description)) {
      nextItemErrors.description = "Item description is required";
    }
    if (isBlank(item.unit) || Number(item.unit) <= 0) {
      nextItemErrors.unit = "Units must be greater than 0";
    }
    if (isBlank(item.unitPrice) || Number(item.unitPrice) <= 0) {
      nextItemErrors.unitPrice = "Unit price must be greater than 0";
    }

    if (Object.keys(nextItemErrors).length > 0) {
      setItemErrors(nextItemErrors);
      toast.error("กรุณากรอกข้อมูลให้ครบทุกช่อง ❌");
      return;
    }
    setQuotationData((prev) => ({
      ...prev,
      items: [...prev.items, item],
    }));
    setItem({ description: "", unit: 1, unitPrice: "" });
    setItemErrors({});
    setFormErrors((prev) => {
      if (!prev.items) return prev;
      const next = { ...prev };
      delete next.items;
      return next;
    });
  };

  const removeItem = (index) => {
    setQuotationData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateItem = (index, updatedItem) => {
    setQuotationData((prev) => ({
      ...prev,
      items: prev.items.map((itm, i) => (i === index ? updatedItem : itm)),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuotationData((prev) => ({
      ...prev,
      [name]:
        name === "discount" || name === "fee" ? parseFloat(value) || 0 : value,
    }));
    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    requiredFields.forEach(([field, label]) => {
      if (isBlank(quotationData[field])) {
        nextErrors[field] = `${label} is required`;
      }
    });

    if (
      quotationData.startDate &&
      quotationData.endDate &&
      new Date(quotationData.endDate) < new Date(quotationData.startDate)
    ) {
      nextErrors.endDate = "End Date must be after Start Date";
    }

    if (quotationData.items.length === 0) {
      nextErrors.items = "Please add at least one item";
    }

    setFormErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      toast.error("กรุณากรอกข้อมูลที่จำเป็นให้ครบ");
      return;
    }

    setIsLoading(true);

    const processedItems = quotationData.items.map((item) => ({
      ...item,
      unitPrice: roundUp(item.unitPrice),
      amount: roundUp(item.unit * roundUp(item.unitPrice)),
    }));

    const totalBeforeFee = roundUp(
      processedItems.reduce((sum, itm) => sum + itm.amount, 0)
    );
    const calFee = roundUp(totalBeforeFee * (quotationData.fee / 100));
    const total = roundUp(totalBeforeFee + calFee);
    const amountBeforeTax = roundUp(total - quotationData.discount);
    const vat = roundUp(amountBeforeTax * 0.07);
    const netAmount = roundUp(amountBeforeTax + vat);

    const formattedData = {
      ...quotationData,
      totalBeforeFee,
      fee: roundUp(quotationData.fee),
      calFee,
      total,
      discount: roundUp(quotationData.discount),
      amountBeforeTax,
      vat,
      netAmount,
      items: processedItems,
      isDetailedForm,
      isSpecialForm,
      isDraft, // ✅ บอก backend ว่าเป็น Draft หรือไม่
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${apiURL}quotations`, formattedData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      upsertCachedQuotation(response.data);

      if (isDraft) {
        toast.success("บันทึกแบบร่างสำเร็จ!");
        const quotationId = response.data._id;
        navigate(`/quotations/edit/${quotationId}`);
      } else {
        toast.success("สร้างใบเสนอราคาสำเร็จ!");
        const quotationId = response.data._id;
        navigate(`/request-approve-flow/${quotationId}`);
      }
    } catch (error) {
      console.error("Error creating quotation:", error);
      toast.error("ไม่สามารถสร้างใบเสนอราคาได้ ❌");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = async () => {
    setIsLoading(true);
    try {
      if (!quotationData.clientId) {
        toast.error("กรุณาเลือก Client ก่อน Preview ❌");
        setIsLoading(false);
        return;
      }

      const user = JSON.parse(localStorage.getItem("user")) || {};
      const company = user.company || "";
      const bankInfo = bankAccounts.companies[company] || {};

      const totalBeforeFee = quotationData.items.reduce(
        (sum, itm) => sum + itm.unit * itm.unitPrice,
        0
      );
      const feeAmount = quotationData.fee; // ✅ ใช้เป็นจำนวนเงินตรง ๆ
      const totalAfterFee = totalBeforeFee + feeAmount;
      const amountBeforeTax = totalAfterFee - quotationData.discount;
      const vat = amountBeforeTax * 0.07;
      const netAmount = amountBeforeTax + vat;

      const clientResponse = await axios.get(
        `${apiURL}clients/${quotationData.clientId}`
      );
      const clientDetails = clientResponse.data;

      const updatedQuotationData = {
        ...quotationData,
        totalBeforeFee,
        feeAmount,
        total: totalAfterFee,
        amountBeforeTax,
        vat,
        netAmount,
        clientDetails,
        isSpecialForm,
      };

      const blob = await pdf(
        <QuotationPreview
          quotationData={updatedQuotationData}
          bankInfo={bankInfo}
          isDetailedForm={isDetailedForm}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const newTab = window.open();
      if (newTab) {
        newTab.document.write(
          `<iframe src="${url}" style="width:100%;height:100%;border:none;"></iframe>`
        );
      }
    } catch (error) {
      console.error("Error generating preview:", error);
      toast.error("ไม่สามารถสร้างตัวอย่าง PDF ได้ ❌");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClientChange = (clientId, clientName) => {
    setQuotationData((prev) => ({
      ...prev,
      clientId,
      client: clientName,
    }));
    setFormErrors((prev) => {
      if (!prev.clientId) return prev;
      const next = { ...prev };
      delete next.clientId;
      return next;
    });
  };

  return (
    <div className="px-4 sm:px-6 py-5 relative bg-gray-50 dark:bg-gray-900 transition-colors duration-300 min-h-screen">
      <LoadingOverlay loading={isLoading} />

      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm rounded-xl p-4 sm:p-6 transition-all">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-2">
            <BackButton label="ย้อนกลับ" />
          </div>

          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 leading-tight">
              Create Quotation
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              กรอกข้อมูลรายละเอียดใบเสนอราคาให้ครบถ้วน
              ก่อนบันทึกหรือสร้างตัวอย่าง PDF
            </p>
          </div>
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit} noValidate>
          <CreateQuotationForm
            quotationData={quotationData}
            handleChange={handleChange}
            setQuotationData={setQuotationData}
            handleClientChange={handleClientChange}
            errors={formErrors}
          />

          <div className="mt-6 sm:mt-8">
            <ItemsForm
              items={quotationData.items}
              item={item}
              handleItemChange={handleItemChange}
              addItem={addItem}
              removeItem={removeItem}
              updateItem={updateItem}
              isDetailedForm={isDetailedForm}
              setIsDetailedForm={setIsDetailedForm}
              isSpecialForm={isSpecialForm}
              setIsSpecialForm={setIsSpecialForm}
              itemErrors={itemErrors}
              itemsError={formErrors.items}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 mt-8 border-t border-gray-100 dark:border-gray-700 pt-5">
            {/* Preview */}
            <button
              type="button"
              onClick={handlePreview}
              className="w-full sm:w-auto px-5 py-2.5 rounded-md bg-gray-100 dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              disabled={isLoading}
            >
              Preview
            </button>

            {/* Save Draft */}
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              className="w-full sm:w-auto px-5 py-2.5 rounded-md bg-cyan-50 dark:bg-cyan-900/30 text-sm font-medium text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100 dark:hover:bg-cyan-900/50 transition"
              disabled={isLoading}
            >
              Save Draft
            </button>

            {/* Save Quotation */}
            <button
              type="submit"
              className={`w-full sm:w-auto px-5 py-2.5 rounded-md text-sm font-medium transition
              ${
                isLoading
                  ? "bg-cyan-200 dark:bg-cyan-400/30 text-cyan-400 dark:text-cyan-300 cursor-not-allowed"
                  : "bg-[#00ADB5] hover:bg-[#019C9C] dark:bg-[#019C9C] dark:hover:bg-[#00ADB5] text-white"
              }`}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Quotation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default CreateQuotation;
